import { Box, CircularProgress, FormControl, ListItemText, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import CommonMultiSelect, { SelectOption } from "../CommonMultiSelect";
import { fieldRootSx, FIELD_HEIGHT } from "./fieldStyles";

export type { SelectOption };

const LOADING_SIZE = 16;
const MENU_MAX_HEIGHT = 384;
const MENU_WIDTH = 250;
const PLACEHOLDER_COLOR = "#bfbfbf";
const ICON_WIDTH = 28;

const OptionIcon = ({ src }: { src?: string }) =>
  src ? <Box component="img" src={src} alt="" sx={{ width: ICON_WIDTH, mr: 1, objectFit: "contain" }} /> : null;

interface BaseProps {
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  /** Injected by antd Form.Item when validation fails */
  "aria-invalid"?: boolean | "true" | "false";
}
interface SingleProps extends BaseProps {
  multiple?: false;
  value?: string | number | null;
  onChange?: (value: string | number | null, option?: SelectOption) => void;
  /** When set, adds a first item with this label that clears the selection */
  emptyLabel?: string;
  /** Show the search box inside the menu (default true) */
  searchable?: boolean;
  /** Menu width follows the dropdown width instead of the fixed default */
  matchAnchorWidth?: boolean;
}
interface MultipleProps extends BaseProps {
  multiple: true;
  value?: Array<string | number>;
  onChange?: (values: Array<string | number>) => void;
  onClose?: () => void;
}
export type SelectFieldProps = SingleProps | MultipleProps;

/** Common dropdown (MUI). Same look as "National" in category AddModal; `multiple` switches multi-select on. */
const SelectField = (props: SelectFieldProps) => {
  if (props.multiple) {
    const { options, value, onChange, placeholder, disabled, loading, fullWidth, onClose } = props;
    return (
      <CommonMultiSelect
        options={options}
        value={value ?? []}
        onChange={(values) => onChange?.(values)}
        onClose={onClose}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        fullWidth={fullWidth}
        size="medium"
      />
    );
  }
  return <SingleSelect {...props} />;
};

const SingleSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  fullWidth = true,
  emptyLabel,
  searchable = true,
  matchAnchorWidth = false,
  "aria-invalid": ariaInvalid,
}: SingleProps) => {
  const invalid = ariaInvalid === true || ariaInvalid === "true";
  const [searchTerm, setSearchTerm] = useState("");
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;
  }, [options, searchTerm]);

  const handleChange = (e: SelectChangeEvent<string | number>) => {
    const next = e.target.value;
    if (next === "") return onChange?.(null);
    onChange?.(next, options.find((o) => o.value === next));
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled || loading} error={invalid} sx={{ m: 0 }}>
      <Select<string | number>
        displayEmpty
        value={value ?? ""}
        onChange={handleChange}
        onClose={() => setSearchTerm("")}
        sx={fieldRootSx}
        endAdornment={loading ? <CircularProgress size={LOADING_SIZE} sx={{ mr: 4 }} /> : undefined}
        renderValue={(selected) => {
          const option = options.find((o) => o.value === selected);
          return option ? (
            <>
              <OptionIcon src={option.icon} />
              {option.label}
            </>
          ) : (
            <span style={{ color: PLACEHOLDER_COLOR }}>{placeholder}</span>
          );
        }}
        MenuProps={{ autoFocus: false, PaperProps: { style: matchAnchorWidth ? { maxHeight: MENU_MAX_HEIGHT } : { maxHeight: MENU_MAX_HEIGHT, width: MENU_WIDTH } } }}
      >
        {searchable && <MenuItem
          disableRipple
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            cursor: "default",
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "background.paper",
            borderBottom: "1px solid var(--app-border-light)",
            "&:hover": { backgroundColor: "background.paper" },
          }}
        >
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </MenuItem>}
        {emptyLabel && (
          <MenuItem value="" sx={{ minHeight: FIELD_HEIGHT }}>
            <ListItemText primary={emptyLabel} />
          </MenuItem>
        )}
        {filtered.length > 0 ? (
          filtered.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ minHeight: FIELD_HEIGHT }}>
              <OptionIcon src={o.icon} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText primary="No options found" sx={{ textAlign: "center" }} />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default SelectField;
